import React from 'react';

import { addWithConfig } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box, BoxProps, Typography, TypographyProps, withTypography } from 'components/ui';
import { getDesignToken, primitive, spacing } from 'styles/constants';

type ItemOfferProps = BoxProps & {
  $isApplied?: boolean;
  isLoading?: boolean;
};

export const Item = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.item, style],
  });

export const ItemOffer = ({ $isApplied, isLoading, style, ...props }: ItemOfferProps) =>
  React.createElement(Box, {
    ...props,
    style: [
      styles.itemOffer,
      {
        backgroundColor: isLoading
          ? primitive.$blackOpacity10
          : $isApplied
          ? primitive.$successLight
          : primitive.$errorLight,
      },
      style,
    ],
  });

export const ItemOfferFeedback = ({ focusable = true, style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    focusable,
    style: [styles.itemOfferFeedback, style],
  });

export const OfferFeedbackMessage = withTypography({
  fontFamily: 'body',
  fontSize: '2xs',
  color: primitive.$bbqBrown,
  lineHeight: 'sm',
  style: { flex: 1 },
});

/** Flex spacers between actions: grow up to `$4`, shrink when the row is tight. */
export const CartItemEditButtonSpacer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemEditButtonSpacer, style],
  });

export const CartItemEditButtonContainerLeft = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemEditButtonContainerLeft, style],
  });

export const CartItemEditButtonContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemEditButtonContainer, style],
  });

export const CartItemDescription = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemDescription, style],
  });

export const IncentiveName = withTypography({
  m: '$0',
  variant: 'headerTwo',
  focusable: true,
  fontSize: 'sm',
  lineHeight: 'sm',
});

export const CartItemInfoContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemInfoContainer, style],
  });

export const CartItemEditContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemEditContainer, style],
  });

export const CartItemButton = addWithConfig(ActionButton).withConfig({
  variant: ActionButtonVariants.TEXT_ONLY,
  padding: 0,
  margin: 0,
});

export const CartItemButtonText = withTypography({
  fontFamily: 'heading',
  p: '$2',
  lineHeight: '2xs',
});

export const CartItemName = withTypography({
  fontFamily: 'heading',
  fontSize: 'sm',
  m: '$0',
  focusable: true,
  numberOfLines: 2,
});

export const CartItemHeaderText = ({ style, ...props }: TypographyProps) =>
  React.createElement(Typography, {
    fontFamily: 'heading',
    fontSize: 'sm',
    m: '$0',
    focusable: true,
    numberOfLines: 2,
    ...props,
    style: [styles.cartItemHeaderText, style],
  });

export const CartItemNameHeaderContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.cartItemNameHeaderContainer, style],
  });

export const StyledLink = withTypography({
  fontFamily: 'body',
  fontSize: '2xs',
  color: primitive.$bbqBrown,
  bold: true,
  lineHeight: 16,
  style: { flex: 1 },
});

const styles = StyleSheet.create({
  cartItemDescription: {
    alignItems: 'center',
    borderBottomColor: primitive.$blackOpacity10,
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$2,
  },
  cartItemEditButtonContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
  },
  cartItemEditButtonContainerLeft: {
    alignItems: 'center',
    alignSelf: 'stretch',
    flex: 1,
    flexDirection: 'row',
    minWidth: 0,
  },
  cartItemEditButtonSpacer: {
    flex: 1,
    maxWidth: spacing.$4,
    minWidth: 0,
  },
  cartItemEditContainer: {
    alignItems: 'center',
    borderColor: getDesignToken('background-default'),
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$2,
  },
  cartItemHeaderText: {
    flex: 1,
    marginLeft: spacing.$1_5,
    marginRight: spacing.$3,
    minWidth: 0,
    paddingVertical: spacing.$3,
    paddingLeft: spacing.$1_5,
  },
  cartItemInfoContainer: {
    flex: 1,
  },
  cartItemNameHeaderContainer: {
    borderBottomColor: primitive.$blackOpacity10,
    borderBottomWidth: 1,
    borderTopLeftRadius: primitive.$borderRadius,
    borderTopRightRadius: primitive.$borderRadius,
    flexDirection: 'row',
  },
  item: {
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
    marginTop: spacing.$4,
  },
  itemOffer: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderRadius: 8,
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$3,
  },
  itemOfferFeedback: {
    flexDirection: 'row',
  },
});
